import React, { useEffect } from 'react';

import {
  Center,
  Divider,
  Group,
  Loader,
  NoDataMessageCard,
  NoResults,
  SearchInput,
  Skeleton,
  Stack,
  Text,
  Title,
} from 'ui';
import { useInViewport } from 'ui/Hooks';

import { useGetWalletActivity } from '../../../hooks/queries/Ledger/owner';
import { EarningCard } from './EarningCard';

const EarningsList = ({
  search,
  isEarningsEmpty,
}: {
  search: string;
  isEarningsEmpty: boolean;
}) => {
  const { ref, inViewport } = useInViewport();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useGetWalletActivity({
    queryOptions: {
      searches: {
        title: {
          value: search,
        },
      },
    },
  });

  useEffect(() => {
    if (inViewport && hasNextPage) {
      fetchNextPage();
    }
  }, [inViewport, hasNextPage, fetchNextPage]);

  return (
    <Stack gap={0}>
      <Skeleton visible={isLoading}>
        {search && (
          <Text fz={14} fw={600} c="gray">
            Search Results
          </Text>
        )}
        {search && !isLoading && !data?.pages[0].entries.length && <NoResults />}
        {isEarningsEmpty && (
          <NoDataMessageCard
            title="Your Rewards Are Waiting"
            message="Add a card and install the extension to turn your everyday shopping and dining into
              cash back for you or donations for your school."
          />
        )}
        {data?.pages.map((page) =>
          page.entries.map((entry, index) => (
            <React.Fragment key={entry.uuid}>
              <EarningCard key={entry.uuid} entry={entry} />
              {index < page.entries.length - 1 && <Divider size="sm" color="gray.0" />}
            </React.Fragment>
          ))
        )}
        <Center ref={ref} py="xl">
          {isFetchingNextPage && (
            <Group>
              <Loader />
              <Text fz="lg" fw={500} c="gray.2">
                Loading...
              </Text>
            </Group>
          )}
        </Center>
      </Skeleton>
    </Stack>
  );
};

const EarningsContent = ({
  search,
  setSearch,
  isEarningsEmpty,
}: {
  search: string;
  setSearch: (value: string) => void;
  isEarningsEmpty: boolean;
}) => {
  return (
    <Stack>
      <Title order={2}>{'Rewards Activity'}</Title>
      <SearchInput
        disabled={isEarningsEmpty}
        placeholder={'Search rewards activity'}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <EarningsList search={search} isEarningsEmpty={isEarningsEmpty} />
    </Stack>
  );
};

export { EarningsContent };
